/**
 * Opponent Joker Preview - Shows opponent's equipped jokers for counterplay strategy
 */

import React from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { JokerDefinition, describeJokerEffect } from '@solante/shared';

interface OpponentJokerPreviewProps {
  jokers: JokerDefinition[];
  opponentName?: string;
}

import { RARITY_COLORS, RARITY_BADGE_COLORS } from '../../../utils/gameConstants';

export const OpponentJokerPreview: React.FC<OpponentJokerPreviewProps> = ({
  jokers,
  opponentName = 'Opponent',
}) => {
  if (jokers.length === 0) {
    return null;
  }

  return (
    <Tooltip.Provider>
      <div className="rounded-xl border border-red-400/20 bg-red-500/5 p-2">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] uppercase tracking-wide text-red-300/70">
            {opponentName}'s Jokers
          </span>
          <span className="text-[10px] text-white/40">({jokers.length})</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {jokers.map((joker) => (
            <Tooltip.Root key={joker.id} delayDuration={100}>
              <Tooltip.Trigger asChild>
                <div
                  className={`cursor-help rounded-lg border px-2 py-1 text-xs transition hover:scale-105 ${
                    RARITY_COLORS[joker.rarity] ?? RARITY_COLORS['common']
                  }`}
                >
                  <span className="font-semibold text-white text-[11px]">{joker.name}</span>
                </div>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content className="arcade-tooltip border-red-400/30" sideOffset={5}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-white">{joker.name}</span>
                    <span
                      className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${
                        RARITY_BADGE_COLORS[joker.rarity] ?? RARITY_BADGE_COLORS['common']
                      }`}
                    >
                      {joker.rarity}
                    </span>
                  </div>
                  <p className="text-white/60 mb-1">{joker.description}</p>
                  {joker.effects.length > 0 && (
                    <ul className="space-y-0.5 border-t border-white/10 pt-1.5">
                      {joker.effects.map((effect, i) => (
                        <li key={i} className="text-xs text-red-300">
                          + {describeJokerEffect(effect)}
                        </li>
                      ))}
                    </ul>
                  )}
                  <Tooltip.Arrow className="fill-black/95" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          ))}
        </div>
      </div>
    </Tooltip.Provider>
  );
};
